import React, { useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ReferenceLine
} from 'recharts';
import { Paper, Typography, Grid, Box, Button, Avatar, List, ListItem, ListItemIcon, ListItemText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, LinearProgress, Card, CardContent, Divider, IconButton, Tabs, Tab, useTheme } from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import PsychologyIcon from '@mui/icons-material/Psychology';
import GroupsIcon from '@mui/icons-material/Groups';
import BrushIcon from '@mui/icons-material/Brush';
import BusinessIcon from '@mui/icons-material/Business';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { green, red, orange, grey } from '@mui/material/colors';
import CheckIcon from '@mui/icons-material/Check';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ErrorIcon from '@mui/icons-material/Error';
import BlockIcon from '@mui/icons-material/Block';
import { alpha } from '@mui/material/styles';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import GapCompetencyMatrix from '../GapCompetencyMatrix';
import TimeCompetencyRadar from '../TimeCompetencyRadar';
import SkillCluster from '../SkillCluster';
import FlowStateHeatMap from '../FlowStateHeatMap';
// import HexagonalProfile from '../HexagonalProfile';
import DownloadIcon from '@mui/icons-material/Download';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PersonIcon from '@mui/icons-material/Person';
import TimelineIcon from '@mui/icons-material/Timeline';
import ActivityTimeline from '../ActivityTimeline';
import DevelopmentPriorityMatrix from '../DevelopmentPriorityMatrix';
import { Tooltip } from '@mui/material';
import TypeExplanation from '../TypeExplanation';
import FlowStateLegend from '../FlowStateLegend';
import TimeDiscrepancyChart from '../TimeDiscrepancyChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Overview from './Overview';
import Details from './Details';
import AssignmentIcon from '@mui/icons-material/Assignment';
import OrderedLists from '../OrderedLists';
import { TYPE_MAPPING } from '../../constants/typeMapping';

const FLOW_MATCH_THRESHOLD = 0.15;  // Changed from 0.2 - skills must be closer matched
const FLOW_HIGH_ENGAGEMENT = 0.7;   // Changed from 0.6 - requires higher engagement
const KEY_COMPETENCY_THRESHOLD = 0.6;

export const PersonalityTypes = {
  REALISTE: {
    icon: <WorkIcon />,
    color: '#FF4444',
    description: "Vous êtes une personne pratique qui aime travailler avec des objets physiques.",
    label: "Réaliste"
  },
  INVESTIGATEUR: {
    icon: <PsychologyIcon />,
    color: '#00BFB3',
    description: "Vous êtes analytique et aimez résoudre des problèmes complexes.",
    label: "Investigateur"
  },
  ARTISTIQUE: {
    icon: <BrushIcon />,
    color: '#0099CC',
    description: "Vous êtes créatif et appréciez l'expression artistique.",
    label: "Artistique"
  },
  SOCIAL: {
    icon: <GroupsIcon />,
    color: '#66BB6A',
    description: "Vous aimez travailler avec les autres et les aider.",
    label: "Social"
  },
  ENTREPRENEUR: {
    icon: <BusinessIcon />,
    color: '#FFB74D',
    description: "Vous aimez diriger et persuader les autres.",
    label: "Entrepreneur"
  },
  CONVENTIONNEL: {
    icon: <AssignmentIcon />,
    color: '#FF5252',
    description: "Vous êtes une personne organisée qui aime suivre des procédures établies.",
    label: "Conventionnel"
  }
};

const ensureValidData = (results) => {
  if (!results || !results.questions) return null;
  
  const requiredKeys = ['time_spent', 'time_desired', 'competency'];
  const hasAllKeys = requiredKeys.every(key => 
    results.questions.some(q => q.questionKey === key)
  );
  
  if (!hasAllKeys) {
    console.warn('Missing required questions:', 
      requiredKeys.filter(key => 
        !results.questions.some(q => q.questionKey === key)
      )
    );
    return null;
  }
  
  return {
    ...results,
    questions: results.questions.map(question => ({
      ...question,
      rankings: question.rankings.map(rank => ({
        ...rank,
        score: typeof rank.score === 'string' ? parseFloat(rank.score) : rank.score
      }))
    }))
  };
};

const calculatePsychologicalType = (activities, typeMapping) => {
  const typeScores = {
    CONVENTIONNEL: 0,
    REALISTE: 0,
    INVESTIGATEUR: 0,
    ENTREPRENEUR: 0,
    SOCIAL: 0,
    ARTISTIQUE: 0
  };

  const typeItemCounts = {
    CONVENTIONNEL: typeMapping.CONVENTIONNEL.length,
    REALISTE: typeMapping.REALISTE.length,
    INVESTIGATEUR: typeMapping.INVESTIGATEUR.length,
    ENTREPRENEUR: typeMapping.ENTREPRENEUR.length,
    SOCIAL: typeMapping.SOCIAL.length,
    ARTISTIQUE: typeMapping.ARTISTIQUE.length
  };

  activities.forEach(activity => {
    const score = parseFloat(activity.score);
    Object.entries(typeMapping).forEach(([type, items]) => {
      if (items.includes(activity.item)) {
        typeScores[type] += score;
      }
    });
  });

  const normalizedScores = Object.fromEntries(
    Object.entries(typeScores).map(([type, score]) => [
      type,
      score / typeItemCounts[type]
    ])
  );

  return Object.entries(normalizedScores)
    .sort(([,a], [,b]) => b - a)[0][0];
};

const calculateFlowState = (timeDesired, competency) => {
  const desiredScore = parseFloat(timeDesired);
  const skillScore = parseFloat(competency);
  
  // Main zones
  if (skillScore >= 0.66 && desiredScore >= 0.66) {
    return 'FLOW';
  } else if (skillScore < 0.33 && desiredScore >= 0.66) {
    return 'ANXIÉTÉ';
  } else if (skillScore >= 0.66 && desiredScore < 0.33) {
    return 'ENNUI';
  } else if (skillScore < 0.33 && desiredScore < 0.33) {
    return 'INDIFFÉRENCE';
  }
  
  // Buffer zones
  if (skillScore >= 0.33 && skillScore < 0.66) {
    if (desiredScore >= 0.66) {
      return 'EXCITATION';
    } else if (desiredScore < 0.33) {
      return 'DETACHEMENT';
    } else {
      return 'NEUTRE';
    }
  }
  
  if (desiredScore >= 0.33 && desiredScore < 0.66) {
    if (skillScore >= 0.66) {
      return 'MAITRISE';
    } else if (skillScore < 0.33) {
      return 'INQUIETUDE';
    } else {
      return 'NEUTRE';
    }
  }
  
  return 'NEUTRE';
};

// Avatar Component for Personality Type
const PersonalityAvatar = ({ type }) => {
  const typeInfo = PersonalityTypes[type];
  
  return (
    <Avatar sx={{ bgcolor: typeInfo.color, width: 100, height: 100 }}>
      {typeInfo.icon}
    </Avatar>
  );
};

const calculateTypeCompetencies = (competencyData, typeMapping) => {
  const typeScores = {};
  const typeTopItems = {};
  
  // Initialize scores and items arrays for each type
  Object.keys(typeMapping).forEach(type => {
    typeScores[type] = [];
    typeTopItems[type] = [];
  });

  // Map competencies to types
  competencyData.forEach(item => {
    Object.entries(typeMapping).forEach(([type, items]) => {
      if (items.includes(item.item)) {
        typeScores[type].push({
          score: parseFloat(item.score),
          activity: item.item
        });
      }
    });
  });

  // Calculate averages and get top items
  const results = {};
  Object.entries(typeScores).forEach(([type, scores]) => {
    if (scores.length === 0) {
      results[type] = {
        average: 0,
        topItems: []
      };
    } else {
      // Sort scores to get top items
      const sortedScores = [...scores].sort((a, b) => b.score - a.score);
      results[type] = {
        average: scores.reduce((sum, item) => sum + item.score, 0) / scores.length,
        topItems: sortedScores.slice(0, 3)
      };
    }
  });

  return results;
};

const DashboardCard = ({ title, content, description, color }) => (
  <Tooltip 
    title={
      <Typography sx={{ p: 1 }}>
        {description}
      </Typography>
    }
    arrow
  >
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography color="textSecondary" variant="subtitle2" gutterBottom>
              {title}
            </Typography>
            {content}
          </Box>
          <Box sx={{ 
            backgroundColor: `${color}15`, 
            p: 1, 
            borderRadius: '50%' 
          }}>
            {content && content.props && content.props.type && 
              React.cloneElement(PersonalityTypes[content.props.type].icon, {
                sx: { color: color, fontSize: 24 }
              })
            }
          </Box>
        </Box>
      </CardContent>
    </Card>
  </Tooltip>
);

const TabPanel = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    data-index={index}
    sx={{ 
      display: value !== index ? 'none' : 'block',
      minHeight: '400px',
      '&[style*="display: block"]': {
        pageBreakInside: 'avoid',
        marginBottom: '20px'
      }
    }}
  >
    {children}
  </Box>
);

const MetricsTab = ({ results }) => {
  const theme = useTheme();
  
  // Calculate average metrics across all questions
  const averageMetrics = useMemo(() => {
    const questionMetrics = results.questions.map(q => q.metrics || {});
    return {
      coherence: questionMetrics.reduce((acc, m) => acc + (m.coherence || 0), 0) / questionMetrics.length,
      responseTime: questionMetrics.reduce((acc, m) => acc + (m.responseTime || 0), 0) / questionMetrics.length,
      nuance: questionMetrics.reduce((acc, m) => acc + (m.nuance || 0), 0) / questionMetrics.length,
    };
  }, [results]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Métriques de Qualité par Question
      </Typography>
      
      {results.questions.map((question, index) => (
        <Paper key={index} sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {question.questionKey === 'time_spent' ? 'Temps Passé' :
             question.questionKey === 'time_desired' ? 'Temps Souhaité' :
             'Compétences'}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Cohérence
                </Typography>
                <Typography variant="h6">
                  {((question.metrics?.coherence || 0) * 100).toFixed(0)}%
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Temps de Réponse
                </Typography>
                <Typography variant="h6">
                  {((question.metrics?.responseTime || 0) * 100).toFixed(0)}%
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Nuance
                </Typography>
                <Typography variant="h6">
                  {((question.metrics?.nuance || 0) * 100).toFixed(0)}%
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      ))}

      <Paper sx={{ p: 3, mt: 4, bgcolor: theme.palette.primary.light }}>
        <Typography variant="h6" gutterBottom color="white">
          Moyennes Globales
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" color="white">
                Cohérence Moyenne
              </Typography>
              <Typography variant="h5" color="white">
                {(averageMetrics.coherence * 100).toFixed(0)}%
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" color="white">
                Temps de Réponse Moyen
              </Typography>
              <Typography variant="h5" color="white">
                {(averageMetrics.responseTime * 100).toFixed(0)}%
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" color="white">
                Nuance Moyenne
              </Typography>
              <Typography variant="h5" color="white">
                {(averageMetrics.nuance * 100).toFixed(0)}%
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

const TypeBadge = ({ type }) => {
  const typeInfo = PersonalityTypes[type];

  if (!typeInfo) {
    console.warn(`TypeBadge: Unknown type "${type}"`);
    return null; // or return a default icon/color
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {React.cloneElement(typeInfo.icon, {
        sx: { color: typeInfo.color, fontSize: 24 }
      })}
      <Typography 
        sx={{ 
          color: typeInfo.color,
          fontWeight: 'bold'
        }}
      >
        {typeInfo.label}
      </Typography>
    </Box>
  );
};

const Results = ({ results }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const theme = useTheme();
  const resultsRef = useRef(null);
  const validatedResults = ensureValidData(results);

  // Add MetricsTab to tabs
  const tabs = [
    { label: 'Vue d\'ensemble', component: <Overview results={validatedResults} /> },
    { label: 'Détails', component: <Details results={validatedResults} /> },
    { label: 'Métriques', component: <MetricsTab results={validatedResults} /> }
  ];

  // All useMemo hooks must be at the top level
  const gapData = useMemo(() => {
    if (!validatedResults) return [];
    const currentActivities = validatedResults.questions.find(q => q.questionKey === 'time_spent')?.rankings || [];
    const desiredActivities = validatedResults.questions.find(q => q.questionKey === 'time_desired')?.rankings || [];
    
    return currentActivities.map(current => {
      const desired = desiredActivities.find(d => d.item === current.item);
      return {
        activity: current.item,
        current: parseFloat(current.score),
        desired: parseFloat(desired?.score || 0),
        gap: parseFloat((desired?.score - current.score || 0).toFixed(3))
      };
    }).sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap)).slice(0, 18);
  }, [validatedResults]);

  const dominantType = useMemo(() => {
    if (!validatedResults) return null;
    const desiredActivities = validatedResults.questions.find(q => q.questionKey === 'time_desired')?.rankings || [];
    
    return calculatePsychologicalType(desiredActivities, TYPE_MAPPING);
  }, [validatedResults]);

  const flowAnalysis = useMemo(() => {
    if (!validatedResults) return [];
    const desiredActivities = validatedResults.questions.find(q => q.questionKey === 'time_desired')?.rankings || [];
    const competencies = validatedResults.questions.find(q => q.questionKey === 'competency')?.rankings || [];
    
    return desiredActivities.map(desired => {
      const competency = competencies.find(c => c.item === desired.item);
      return {
        activity: desired.item,
        state: calculateFlowState(desired.score, competency?.score || 0),
        challenge: parseFloat(desired.score),
        skill: parseFloat(competency?.score || 0)
      };
    });
  }, [validatedResults]);

  const currentDominantType = useMemo(() => {
    if (!validatedResults) return null;
    const currentActivities = validatedResults.questions.find(q => q.questionKey === 'time_spent')?.rankings || [];
    
    return calculatePsychologicalType(currentActivities, TYPE_MAPPING);
  }, [validatedResults]);

  const isAligned = useMemo(() => 
    dominantType === currentDominantType, 
    [dominantType, currentDominantType]
  );

  const mismatchDetails = useMemo(() => 
    gapData.filter(item => item.gap < -0.2 || item.gap > 0.2),
    [gapData]
  );

  const recommendations = useMemo(() => {
    if (!validatedResults) return { strengthen: [], reduce: [] };
    
    const timeSpent = validatedResults.questions.find(q => q.questionKey === 'time_spent')?.rankings || [];
    const timeDesired = validatedResults.questions.find(q => q.questionKey === 'time_desired')?.rankings || [];
    
    return timeSpent.reduce((acc, current) => {
      const desired = timeDesired.find(d => d.item === current.item);
      if (!desired) return acc;
      
      const gap = desired.score - current.score;
      if (gap > KEY_COMPETENCY_THRESHOLD) {
        acc.strengthen.push(current.item);
      } else if (gap < -KEY_COMPETENCY_THRESHOLD) {
        acc.reduce.push(current.item);
      }
      return acc;
    }, { strengthen: [], reduce: [] });
  }, [validatedResults]);

  const typeCompetencies = useMemo(() => {
    if (!validatedResults) return {};
    const competencyData = validatedResults.questions.find(q => q.questionKey === 'competency')?.rankings || [];
    return calculateTypeCompetencies(competencyData, TYPE_MAPPING);
  }, [validatedResults]);

  // Add this calculation for competency type
  const competencyDominantType = useMemo(() => {
    if (!validatedResults) return null;
    const competencyActivities = validatedResults.questions.find(q => q.questionKey === 'competency')?.rankings || [];
    return calculatePsychologicalType(competencyActivities, TYPE_MAPPING);
  }, [validatedResults]);

  const isEfficient = useMemo(() => {
    if (!validatedResults) return false;
    return currentDominantType === competencyDominantType;
  }, [currentDominantType, competencyDominantType]);

  // Early return after all hooks
  if (!validatedResults) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Données insuffisantes pour générer les résultats
        </Typography>
      </Box>
    );
  }

  const handleExportPDF = async () => {
    try {
      const element = resultsRef.current;
      
      // Store current tab
      const originalTab = currentTab;
      
      // Create PDF with landscape orientation
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      // A4 landscape dimensions
      const pageWidth = 297; // mm
      const pageHeight = 210; // mm
      const margin = 10; // mm
      
      // Get all tab panels
      const tabPanels = element.querySelectorAll('[role="tabpanel"]');
      
      // Process each tab panel separately
      for (let i = 0; i < tabPanels.length; i++) {
        const panel = tabPanels[i];
        
        // Skip hidden panels
        if (panel.hidden) {
          // Make panel visible temporarily
          panel.hidden = false;
          panel.style.display = 'block';
          panel.style.visibility = 'visible';
        }
        
        // Wait for content to render
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Create a temporary container for this panel
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.width = '1200px';
        tempContainer.style.backgroundColor = '#ffffff';
        tempContainer.style.padding = '20px';
        document.body.appendChild(tempContainer);
        
        // Clone the panel to the temporary container
        const panelClone = panel.cloneNode(true);
        tempContainer.appendChild(panelClone);
        
        // Make all content in the clone visible
        panelClone.style.display = 'block';
        panelClone.style.visibility = 'visible';
        panelClone.style.height = 'auto';
        panelClone.style.position = 'static';
        panelClone.style.overflow = 'visible';
        panelClone.hidden = false;
        
        // Make all charts and graphs visible
        const charts = panelClone.querySelectorAll('canvas, svg, .recharts-wrapper');
        charts.forEach(chart => {
          chart.style.display = 'block';
          chart.style.visibility = 'visible';
          chart.style.maxWidth = '100%';
          chart.style.height = 'auto';
        });
        
        // Add tab title to the panel
        const tabTitle = document.createElement('h2');
        const tabIndex = panel.getAttribute('data-index');
        const tabLabel = element.querySelector(`[role="tab"][data-index="${tabIndex}"]`)?.textContent || `Tab ${i+1}`;
        tabTitle.textContent = tabLabel;
        panelClone.insertBefore(tabTitle, panelClone.firstChild);
        
        // Wait for content to render
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Capture this panel
        const canvas = await html2canvas(panelClone, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: 1200,
          height: panelClone.scrollHeight,
          windowWidth: 1200,
          imageTimeout: 10000
        });
        
        // Add a new page for each panel except the first one
        if (i > 0) {
          pdf.addPage();
        }
        
        // Calculate image dimensions preserving aspect ratio
        const imgWidth = pageWidth - (margin * 2);
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // If the image is taller than the page, we need to split it
        if (imgHeight > pageHeight - (margin * 2)) {
          // Calculate how many pages we need
          const pageCount = Math.ceil(imgHeight / (pageHeight - (margin * 2)));
          
          for (let j = 0; j < pageCount; j++) {
            // Add a new page for each additional page except the first one
            if (j > 0) {
              pdf.addPage();
            }
            
            // Calculate the portion of the image to show on this page
            const sourceY = j * (canvas.height / pageCount);
            const sourceHeight = canvas.height / pageCount;
            
            // Create a temporary canvas for this portion
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = canvas.width;
            tempCanvas.height = sourceHeight;
            const tempCtx = tempCanvas.getContext('2d');
            
            // Draw the portion of the original canvas
            tempCtx.drawImage(
              canvas,
              0, sourceY, canvas.width, sourceHeight,
              0, 0, tempCanvas.width, tempCanvas.height
            );
            
            // Add this portion to the PDF
            const partialImgData = tempCanvas.toDataURL('image/jpeg', 1.0);
            pdf.addImage(
              partialImgData,
              'JPEG',
              margin,
              margin,
              imgWidth,
              (sourceHeight * imgWidth) / canvas.width
            );
          }
        } else {
          // The image fits on a single page
          const imgData = canvas.toDataURL('image/jpeg', 1.0);
          pdf.addImage(
            imgData,
            'JPEG',
            margin,
            margin,
            imgWidth,
            imgHeight
          );
        }
        
        // Clean up the temporary container
        document.body.removeChild(tempContainer);
        
        // Restore panel visibility
        if (panel.getAttribute('data-index') != originalTab) {
          panel.hidden = true;
          panel.style.display = '';
          panel.style.visibility = '';
        }
      }
      
      // Restore original tab
      setCurrentTab(originalTab);
      
      // Save PDF with a descriptive filename
      const date = new Date().toISOString().slice(0, 10);
      pdf.save(`resultats-analyse-${date}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Show error message to user
      alert('Une erreur est survenue lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }} ref={resultsRef}>
      <Box sx={{ flexGrow: 1, p: 3, maxWidth: '1600px', mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">Vos Résultats</Typography>
          <IconButton onClick={handleExportPDF} color="primary">
            <DownloadIcon />
          </IconButton>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard
              title="Type Actuel"
              content={<TypeBadge type={currentDominantType} />}
              description={PersonalityTypes[currentDominantType].description}
              color={PersonalityTypes[currentDominantType].color}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard
              title="Type Souhaité"
              content={<TypeBadge type={dominantType} />}
              description={PersonalityTypes[dominantType].description}
              color={PersonalityTypes[dominantType].color}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard
              title="Type Compétences"
              content={<TypeBadge type={competencyDominantType} />}
              description={PersonalityTypes[competencyDominantType].description}
              color={PersonalityTypes[competencyDominantType].color}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <DashboardCard
              title="Efficacité"
              content={
                <Typography variant="h6" color={isEfficient ? theme.palette.success.main : theme.palette.error.main}>
                  {isEfficient ? "Optimal" : "Non Optimal"}
                </Typography>
              }
              description="Compare votre type d'activités actuelles avec vos compétences dominantes. L'efficacité est optimale quand ces deux types correspondent."
              color={isEfficient ? theme.palette.success.main : theme.palette.error.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <DashboardCard
              title="Alignement"
              content={
                <Typography variant="h6" color={isAligned ? theme.palette.success.main : theme.palette.error.main}>
                  {isAligned ? "Aligné" : "Non Aligné"}
                </Typography>
              }
              description="Compare votre type de personnalité actuel (basé sur le temps passé) avec votre type Souhaité. L'alignement est parfait quand ces deux types correspondent."
              color={isAligned ? theme.palette.success.main : theme.palette.error.main}
            />
          </Grid>
        </Grid>

        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Vue d'ensemble" />
            <Tab label="Analyse détaillée" />
            <Tab label="Explication des types" />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Paper 
                sx={{ 
                  p: 3, 
                  height: 500,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <FlowStateHeatMap flowAnalysis={flowAnalysis} />
              </Paper>
            </Grid>
            
            <Grid item xs={12} lg={4}>
              <FlowStateLegend />
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper 
                sx={{ 
                  p: 3, 
                  height: 500,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <TimeDiscrepancyChart results={validatedResults} />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: 500 }}>
                <Typography variant="h6" gutterBottom>
                  Recommandations Prioritaires
                </Typography>
                <List>
                  {recommendations.strengthen.length === 0 && recommendations.reduce.length === 0 ? (
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Félicitations !"
                        secondary="Votre emploi du temps est parfaitement aligné avec vos souhaits."
                      />
                    </ListItem>
                  ) : (
                    <>
                      <ListItem>
                        <ListItemIcon>
                          <TimelineIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Activités à Renforcer"
                          secondary={
                            recommendations.strengthen.length > 0 
                              ? recommendations.strengthen.join(', ') 
                              : 'Aucune activité à renforcer'
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <AssessmentIcon color="error" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Activités à Réduire"
                          secondary={
                            recommendations.reduce.length > 0 
                              ? recommendations.reduce.join(', ') 
                              : 'Aucune activité à réduire'
                          }
                        />
                      </ListItem>
                    </>
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Detailed Analysis Tab */}
        <TabPanel value={currentTab} index={1}>
          <Details results={validatedResults} />
        </TabPanel>

        {/* Type Explanation Tab */}
        <TabPanel value={currentTab} index={2}>
          <TypeExplanation />
        </TabPanel>

        {/* Ordered Lists */}
        <Box sx={{ mt: 3 }}>
          <OrderedLists results={validatedResults} />
        </Box>
      </Box>
    </Box>
  );
};

export default Results;